import { useState, useEffect, useCallback } from 'react';
import { Save, History, Trash2, RotateCcw, Database } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export function SnapshotButtons({ module, moduleLabel }) {
  const { api, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'admin';

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/snapshots', { module, label: `Sauvegarde ${moduleLabel}` });
      toast.success(`Sauvegarde effectuee (${res.data.record_count} enregistrements)`);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSave} disabled={saving} data-testid={`snapshot-save-${module}`}>
        <Save className="w-4 h-4" />
        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
      </Button>
      {isAdmin && (
        <RestoreDialog module={module} moduleLabel={moduleLabel} api={api} />
      )}
    </div>
  );
}

function RestoreDialog({ module, moduleLabel, api }) {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/snapshots?module=${module}`);
      setSnapshots(res.data);
    } catch {
      toast.error('Erreur chargement snapshots');
    } finally {
      setLoading(false);
    }
  }, [api, module]);

  useEffect(() => {
    if (open) fetchSnapshots();
  }, [open, fetchSnapshots]);

  const handleRestore = async (snapshot) => {
    const confirmMsg = snapshot.module === 'all'
      ? `Restaurer TOUS les modules au ${formatDateTime(snapshot.created_at)} ?\nLes donnees actuelles seront sauvegardees automatiquement.`
      : `Restaurer ${snapshot.module_label} au ${formatDateTime(snapshot.created_at)} ?\nLes donnees actuelles seront sauvegardees automatiquement.`;
    if (!window.confirm(confirmMsg)) return;
    setRestoring(snapshot.id);
    try {
      const body = snapshot.module === 'all' ? { module } : {};
      await api.post(`/snapshots/${snapshot.id}/restore`, body);
      toast.success('Restauration effectuee. Rechargez la page pour voir les donnees.');
      setOpen(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce snapshot ?')) return;
    try {
      await api.delete(`/snapshots/${id}`);
      setSnapshots(prev => prev.filter(s => s.id !== id));
      toast.success('Snapshot supprime');
    } catch {
      toast.error('Erreur suppression');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid={`snapshot-restore-${module}`}>
          <History className="w-4 h-4" />
          Restaurer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Restaurer — {moduleLabel}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-center text-muted-foreground py-8">Chargement...</p>
        ) : snapshots.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Aucune sauvegarde disponible</p>
        ) : (
          <div className="space-y-2">
            {snapshots.map(s => (
              <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border ${s.is_auto_backup ? 'border-dashed bg-muted/30' : 'hover:bg-muted/50'}`} data-testid={`snapshot-${s.id}`}>
                <Database className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{s.label}</p>
                    {s.is_auto_backup && <Badge variant="outline" className="text-[10px]">auto</Badge>}
                    {s.module === 'all' && <Badge className="text-[10px] bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">global</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(s.created_at)} — {s.created_by} — {s.record_count} enr.
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRestore(s)} disabled={restoring === s.id} title="Restaurer">
                    <RotateCcw className={`w-3.5 h-3.5 text-blue-600 ${restoring === s.id ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(s.id)} title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
